import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { hotelService } from '../../../services/api';

const HotelForm = ({ hotel, isOpen, onClose, onSave, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    address: '',
    city: '',
    state: 'SP', // Estado padrão
    country: 'Brasil',
    phone: '',
    email: '',
    website: '',
    logo: '',
    logoSize: '64',
    isActive: true,
    minChildChargeAge: '',
    childDiscountPercent: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (hotel && mode === 'edit') {
      setFormData({
        name: hotel.name || '',
        code: hotel.code || '',
        description: hotel.description || '',
        address: hotel.address || '',
        city: hotel.city || '',
        state: hotel.state || '',
        country: hotel.country || 'Brasil',
        phone: hotel.phone || '',
        email: hotel.email || '',
        website: hotel.website || '',
        logo: hotel.logo || '',
        logoSize: hotel.logoSize || '64',
        isActive: hotel.isActive ?? true,
        minChildChargeAge: hotel.minChildChargeAge ?? '',
        childDiscountPercent: hotel.childDiscountPercent ?? ''
      });
    } else {
      setFormData({
        name: '',
        code: '',
        description: '',
        address: '',
        city: '',
        state: 'SP', // Estado padrão
        country: 'Brasil',
        phone: '',
        email: '',
        website: '',
        logo: '',
        logoSize: '64',
        isActive: true,
        minChildChargeAge: '',
        childDiscountPercent: ''
      });
    }
    setErrors({});
  }, [hotel, mode, isOpen]);

  // Carregar configurações MIN_CHILD_CHARGE_AGE e CHILD_DISCOUNT_PERCENT ao abrir em modo edição
  useEffect(() => {
    const loadSettings = async () => {
      try {
        if (isOpen && mode === 'edit' && hotel?.id) {
          const [minAgeResp, discountResp] = await Promise.all([
            hotelService.getSetting(hotel.id, 'MIN_CHILD_CHARGE_AGE').catch(() => null),
            hotelService.getSetting(hotel.id, 'CHILD_DISCOUNT_PERCENT').catch(() => null)
          ]);
          
          const minAgeValue = minAgeResp?.value != null ? String(minAgeResp.value) : '';
          const discountValue = discountResp?.value != null ? String(discountResp.value) : '';
          
          setFormData(prev => ({ 
            ...prev, 
            minChildChargeAge: minAgeValue,
            childDiscountPercent: discountValue
          }));
        }
      } catch (e) {
        // Se não existir, manter vazio
        setFormData(prev => ({ 
          ...prev, 
          minChildChargeAge: prev.minChildChargeAge || '',
          childDiscountPercent: prev.childDiscountPercent || ''
        }));
      }
    };
    loadSettings();
  }, [isOpen, mode, hotel]);

  const validateForm = () => {
    const newErrors = {};
    // Validação opcional da idade mínima de cobrança
    if (formData.minChildChargeAge && (isNaN(Number(formData.minChildChargeAge)) || Number(formData.minChildChargeAge) < 0)) {
      newErrors.minChildChargeAge = 'Informe uma idade válida (número positivo)';
    }
    
    // Validação opcional do desconto percentual
    if (formData.childDiscountPercent && (isNaN(Number(formData.childDiscountPercent)) || Number(formData.childDiscountPercent) < 0 || Number(formData.childDiscountPercent) > 100)) {
      newErrors.childDiscountPercent = 'Informe um percentual válido (0 a 100)';
    }

    // Validações básicas dos campos obrigatórios
    if (!formData.name?.trim()) {
      newErrors.name = 'Nome do hotel é obrigatório';
    }

    if (!formData.code?.trim()) {
      newErrors.code = 'Código do hotel é obrigatório';
    } else if (formData.code.trim().length < 3) {
      newErrors.code = 'Código deve ter pelo menos 3 caracteres';
    }

    if (!formData.address?.trim()) {
      newErrors.address = 'Endereço é obrigatório';
    }

    if (!formData.city?.trim()) {
      newErrors.city = 'Cidade é obrigatória';
    }

    if (!formData.state || formData.state === '' || !formData.state.trim()) {
      newErrors.state = 'Estado é obrigatório';
    }

    // Validações opcionais
    if (formData.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (formData.website?.trim() && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website deve começar com http:// ou https://';
    }

    if (formData.logo?.trim() && !/^https?:\/\/.+/.test(formData.logo)) {
      newErrors.logo = 'URL da logo deve começar com http:// ou https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Garantir que todos os campos obrigatórios tenham valores válidos
      const hotelData = {
        name: formData.name.trim(),
        code: formData.code.trim().toLowerCase().replace(/\s+/g, '-'),
        description: formData.description.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state && formData.state.trim() ? formData.state.trim().toUpperCase() : 'SP',
        country: formData.country.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        website: formData.website.trim(),
        logo: formData.logo.trim(),
        logoSize: formData.logoSize,
        isActive: formData.isActive,
        // Campos somente para UI; persistidos em HotelSetting
        minChildChargeAge: formData.minChildChargeAge,
        childDiscountPercent: formData.childDiscountPercent
      };

      // Log para debug
      console.log('🔍 Dados do formulário antes do envio:', formData);
      console.log('📤 Dados processados para envio:', hotelData);

      await onSave(hotelData);
    } catch (error) {
      console.error('Erro ao salvar hotel:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  const getTitle = () => {
    switch (mode) {
      case 'edit':
        return 'Editar Hotel';
      default:
        return 'Adicionar Novo Hotel';
    }
  };

  const states = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-background border border-border rounded-lg shadow-elevation-modal w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Building2" size={20} className="text-primary" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">{getTitle()}</h2>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                iconName="X"
                className="p-2"
              />
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nome do Hotel *"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={errors.name}
                  placeholder="Ex: Hotel Exemplo"
                  required
                />

                <Input
                  label="Código do Hotel *"
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  error={errors.code}
                  placeholder="Ex: hotel-exemplo"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descrição do hotel..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>

              {/* Address Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Endereço *"
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  error={errors.address}
                  placeholder="Rua, número, bairro"
                  required
                />

                <Input
                  label="Cidade *"
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  error={errors.city}
                  placeholder="Nome da cidade"
                  required
                />

                                 <div>
                   <label className="block text-sm font-medium text-foreground mb-2">
                     Estado *
                   </label>
                   <select
                     value={formData.state}
                     onChange={(e) => handleInputChange('state', e.target.value)}
                     className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                     required
                   >
                     {states.map((state) => (
                       <option key={state} value={state}>
                         {state}
                       </option>
                     ))}
                   </select>
                   {errors.state && (
                     <p className="text-sm text-red-500 mt-1">{errors.state}</p>
                   )}
                 </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Telefone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="(11) 1234-5678"
                />

                <Input
                  label="E-mail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={errors.email}
                  placeholder="contato@hotelexemplo.com"
                />
              </div>

              {/* Website and Country */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  error={errors.website}
                  placeholder="https://www.hotelexemplo.com"
                />

                <Input
                  label="País"
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  placeholder="Brasil"
                />
              </div>

              {/* Logo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="URL da Logo"
                  type="url"
                  value={formData.logo}
                  onChange={(e) => handleInputChange('logo', e.target.value)}
                  error={errors.logo}
                  placeholder="https://exemplo.com/logo.png"
                  description="URL da logo do hotel (formato PNG, JPG ou SVG recomendado)"
                />

                <Input
                  label="Tamanho da Logo (px)"
                  type="number"
                  value={formData.logoSize}
                  onChange={(e) => handleInputChange('logoSize', e.target.value)}
                  placeholder="64"
                  min="16"
                  max="200"
                  step="1"
                  description="Tamanho da logo em pixels (16-200px)"
                />
              </div>

              {/* Status */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="rounded border-border"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-foreground">
                  Hotel ativo no sistema
                </label>
              </div>

              {/* Billing Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Idade mínima para cobrar criança"
                  type="number"
                  min={0}
                  value={formData.minChildChargeAge}
                  onChange={(e) => handleInputChange('minChildChargeAge', e.target.value)}
                  placeholder="Ex: 6"
                  error={errors.minChildChargeAge}
                  description="Crianças com idade superior a este valor serão cobradas"
                />
                <Input
                  label="Desconto percentual para crianças (%)"
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={formData.childDiscountPercent}
                  onChange={(e) => handleInputChange('childDiscountPercent', e.target.value)}
                  placeholder="Ex: 30"
                  error={errors.childDiscountPercent}
                  description="Desconto aplicado ao preço solteiro para crianças cobradas"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Save"
                iconPosition="left"
              >
                {mode === 'edit' ? 'Salvar Alterações' : 'Criar Hotel'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default HotelForm;
